import { useCallback, useEffect } from "react";
import instance from "../Interceptor/api_instance";
import { useFetch } from "./useFetch";
import { usePaginatedData } from "./usePaginatedData";

/** 
 * usePaginatedQuery:
 * Combines cursor-based pagination state (usePaginatedData) with async data
 * fetching (useFetch). Automatically fetches whenever the current page changes
 * and exposes a `refetch` helper for on-demand refreshes (e.g. after mutations).
 *
 * @param {string} initialPage - The initial API endpoint URL.
 * @returns {object} Merged pagination state/actions, the raw fetcher, and refetch.
 */
export function usePaginatedQuery(initialPage) {
  // Pagination reducer: tracks currentPage, data[], next/previous cursors, pageNum, etc.
  const paginated = usePaginatedData(initialPage);

  // Generic GET request function consumed by useFetch; URL is passed via args
  const requestFn = useCallback(({ signal, args }) => {
    const [url] = args;
    return instance.get(url, { signal }).then((res) => res.data);
  }, []);

  // useFetch wraps requestFn with abort-controller management and deduplication
  const fetcher = useFetch(requestFn, [requestFn], {
    enabled: false,       // don't run on mount – we trigger manually
    cancelPrevious: true, // abort in-flight request when a new one starts
  });

  /**
   * refetch – convenience wrapper around fetcher.run + setData.
   * Components call this after a mutation to reload the current (or a specific) page.
   * @param {string} [url] - optional URL override; defaults to the current page.
   */
  const refetch = useCallback(
    (url) => {
      const target = url || paginated.state.currentPage;
      return fetcher
        .run(target)
        .then(paginated.setData)
        .catch((err) => {
          if (err?.name !== "AbortError") paginated.setError(err);
        });
    },
    [fetcher, paginated.state.currentPage, paginated.setData, paginated.setError]
  );

  // Fetch data automatically whenever the current page URL changes (e.g. filter or pagination)
  useEffect(() => {
    paginated.fetchData(); // set loading state in the reducer

    fetcher
      .run(paginated.state.currentPage)
      .then(paginated.setData)
      .catch((err) => {
        if (err?.name !== "AbortError") paginated.setError(err);
      });
  }, [paginated.state.currentPage]);

  return { ...paginated, fetcher, refetch };
}
